import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { AnalyticsResponse, apiService } from '../services/api';
import { usePrivacy } from '../contexts/PrivacyContext';
import { ArrowRight, Building2, Sparkles, TrendingUp } from 'lucide-react';
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface TransactionStatsProps {
  analytics: AnalyticsResponse | null;
  loading: boolean;
  onCategoryClick?: (category: string | null) => void;
  onMonthClick?: (month: string) => void;
  activeCategory?: string | null;
  activeMonth?: string | null;
}

interface CumulativeDataPoint {
  day: number;
  thisMonth: number | null;
  lastMonth: number;
}

type ChartMode = 'expense' | 'income';

// Helper function to get current month name
const getCurrentMonthName = () => {
  return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const TransactionStats: React.FC<TransactionStatsProps> = ({
  analytics,
  loading,
  activeCategory,
  activeMonth,
}) => {
  const { isPrivacyMode } = usePrivacy();
  const navigate = useNavigate();
  const [expenseChartData, setExpenseChartData] = useState<CumulativeDataPoint[]>([]);
  const [incomeChartData, setIncomeChartData] = useState<CumulativeDataPoint[]>([]);
  const [chartMode, setChartMode] = useState<ChartMode>('expense');

  useEffect(() => {
    const fetchDailySpend = async () => {
      try {
        const response = await apiService.getDailySpend();
        // Transform the data to match the expected format
        const expenseData = response.expense_data.map(point => ({
          day: point.day,
          thisMonth: point.this_month ? parseFloat(point.this_month.toString()) : null,
          lastMonth: parseFloat(point.last_month.toString())
        }));
        const incomeData = response.income_data.map(point => ({
          day: point.day,
          thisMonth: point.this_month ? parseFloat(point.this_month.toString()) : null,
          lastMonth: parseFloat(point.last_month.toString())
        }));

        setExpenseChartData(expenseData);
        setIncomeChartData(incomeData);
      } catch (err) {
        console.error('Failed to fetch daily spend data:', err);
      }
    };

    // Only fetch chart data when there are no active filters (to avoid duplicate API calls)
    if (!analytics && !activeCategory && !activeMonth) {
      fetchDailySpend();
    }
  }, [analytics, activeCategory, activeMonth]); // Re-fetch when analytics or filters change


  if (!analytics) {
    return null;
  }

  const formatCurrency = (value: string | number) => {
    if (isPrivacyMode) return '$••••';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatCurrencyShort = (value: number) => {
    if (isPrivacyMode) return '$••••';
    if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`;
    }
    return `$${value.toFixed(0)}`;
  };

  // Get chart data based on mode
  const chartData = chartMode === 'expense' ? expenseChartData : incomeChartData;
  const chartColor = chartMode === 'expense' ? 'var(--theme-expense)' : 'var(--theme-income)';

  // Custom tooltip for cumulative spending chart
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      const hasThisMonth = data.thisMonth !== null;

      return (
        <div
          className="rounded-lg shadow-lg min-w-[180px] md:min-w-[220px] p-3 md:p-4"
          style={{
            backgroundColor: 'var(--theme-surface)',
            border: '1px solid color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)'
          }}
        >
          <p className="font-semibold text-xs md:text-sm mb-2 md:mb-3" style={{ color: 'var(--theme-text)' }}>Cumulative Total - Day {data.day}</p>

          <div className="space-y-1.5 md:space-y-2">
            {hasThisMonth && (
              <div className="flex items-center justify-between gap-3 md:gap-4">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full" style={{ backgroundColor: 'var(--theme-primary)' }}></div>
                  <span className="text-[10px] md:text-xs" style={{ color: 'var(--theme-text-secondary)' }}>This Month (Cumulative)</span>
                </div>
                <span className="text-xs md:text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{formatCurrency(data.thisMonth)}</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 md:gap-4">
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full" style={{ backgroundColor: 'var(--theme-text-secondary)' }}></div>
                <span className="text-[10px] md:text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Last Month (Cumulative)</span>
              </div>
              <span className="text-xs md:text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>{formatCurrency(data.lastMonth)}</span>
            </div>
          </div>

          {hasThisMonth && data.lastMonth > 0 && (
            <div
              className="mt-2 md:mt-3 pt-2 md:pt-3"
              style={{ borderTop: '1px solid color-mix(in srgb, var(--theme-text-secondary) 20%, transparent)' }}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] md:text-xs" style={{ color: 'var(--theme-text-secondary)' }}>Difference</span>
                <span className="text-xs md:text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
                  {data.thisMonth - data.lastMonth >= 0 ? '+' : ''}{formatCurrency(Math.abs(data.thisMonth - data.lastMonth))}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Don't show chart when there are active filters to avoid confusion
  const hasActiveFilters = activeCategory || activeMonth;

  // Check if there's any meaningful data to display
  const hasChartData = () => {
    if (expenseChartData.length === 0 && incomeChartData.length === 0) return false;

    // Check if any data point has non-zero values
    const hasExpenseData = expenseChartData.some(point =>
      (point.thisMonth && point.thisMonth > 0) || point.lastMonth > 0
    );
    const hasIncomeData = incomeChartData.some(point =>
      (point.thisMonth && point.thisMonth > 0) || point.lastMonth > 0
    );

    return hasExpenseData || hasIncomeData;
  };

  const showChart = !hasActiveFilters && hasChartData();

  return (
    <div className="mb-4 md:mb-6 space-y-3 md:space-y-4">
      {/* Chart Card - Hidden on mobile to save space, and when filtered */}
      {showChart && (
        <Card className="overflow-hidden hidden md:block">
        <CardContent className="p-4 md:p-6">
          {/* Toggle Header */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>
              {chartMode === 'expense' ? 'Cumulative Spending' : 'Cumulative Income'} - {getCurrentMonthName()}
            </p>

            {/* Toggle Button */}
            <div
              className="flex items-center rounded-lg p-0.5"
              style={{ backgroundColor: 'color-mix(in srgb, var(--theme-text-secondary) 10%, transparent)' }}
            >
              <button
                onClick={() => setChartMode('expense')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  chartMode === 'expense'
                    ? 'shadow-sm'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: chartMode === 'expense' ? 'var(--theme-surface)' : 'transparent',
                  color: chartMode === 'expense' ? 'var(--theme-text)' : 'var(--theme-text-secondary)'
                }}
              >
                Expenses
              </button>
              <button
                onClick={() => setChartMode('income')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  chartMode === 'income'
                    ? 'shadow-sm'
                    : 'hover:opacity-80'
                }`}
                style={{
                  backgroundColor: chartMode === 'income' ? 'var(--theme-surface)' : 'transparent',
                  color: chartMode === 'income' ? 'var(--theme-text)' : 'var(--theme-text-secondary)'
                }}
              >
                Income
              </button>
            </div>
          </div>
          
          {/* Chart */}
          <div className="w-full h-[180px] md:h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={chartData} 
                margin={{ top: 10, right: 10, left: -15, bottom: 10 }}
              >
                <defs>
                  <linearGradient id="colorThisMonthExpense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--theme-expense)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="var(--theme-expense)" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorThisMonthIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--theme-income)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="var(--theme-income)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                
                <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-text-secondary)" strokeOpacity={0.1} vertical={false} horizontal={false} />

                <XAxis
                  dataKey="day"
                  stroke="var(--theme-text-secondary)"
                  tick={{ fontSize: 10, fill: 'var(--theme-text-secondary)' }}
                  tickLine={{ stroke: 'var(--theme-text-secondary)', strokeOpacity: 0.2 }}
                  axisLine={{ stroke: 'var(--theme-text-secondary)', strokeOpacity: 0.2 }}
                  interval="preserveStartEnd"
                />

                <YAxis
                  stroke="var(--theme-text-secondary)"
                  tick={{ fontSize: 10, fill: 'var(--theme-text-secondary)' }}
                  tickLine={{ stroke: 'var(--theme-text-secondary)', strokeOpacity: 0.2 }}
                  axisLine={{ stroke: 'var(--theme-text-secondary)', strokeOpacity: 0.2 }}
                  tickFormatter={formatCurrencyShort}
                  width={45}
                />

                <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'var(--theme-text-secondary)', strokeOpacity: 0.3, strokeWidth: 1 }} />

                {/* This Month Area Fill (translucent) */}
                <Area
                  type="monotone"
                  dataKey="thisMonth"
                  fill={chartMode === 'expense' ? 'url(#colorThisMonthExpense)' : 'url(#colorThisMonthIncome)'}
                  stroke="none"
                  connectNulls={false}
                />

                {/* Last Month Line (dashed gray) */}
                <Line
                  type="monotone"
                  dataKey="lastMonth"
                  stroke="var(--theme-text-secondary)"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  activeDot={{ r: 4, fill: 'var(--theme-text-secondary)', stroke: 'var(--theme-surface)', strokeWidth: 2 }}
                  name="Last Month"
                />

                {/* This Month Line (solid with dynamic color) */}
                <Line
                  type="monotone"
                  dataKey="thisMonth"
                  stroke={chartColor}
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: chartColor, stroke: 'var(--theme-surface)', strokeWidth: 2 }}
                  name="This Month"
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Empty State - shown when no chart data is available */}
      {!hasActiveFilters && !hasChartData() && (
        <div className="apple-card p-8 md:p-12 text-center fade-in-up hidden md:block">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)' }}
          >
            <TrendingUp className="h-8 w-8" style={{ color: 'var(--theme-primary)' }} />
          </div>
          <h3
            className="text-lg font-semibold mb-2"
            style={{ color: 'var(--theme-text)' }}
          >
            Ready to track your spending?
          </h3>
          <p
            className="text-sm mb-6 max-w-sm mx-auto"
            style={{ color: 'var(--theme-text-secondary)' }}
          >
            Connect a bank account to sync transactions, get them auto-categorized with AI, and start tracking your spending patterns.
          </p>
          <Button
            onClick={() => navigate('/accounts')}
            className="gap-2 px-6 py-3 text-sm font-medium rounded-xl transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--theme-primary)',
              color: 'white'
            }}
          >
            <Building2 className="h-4 w-4" />
            Connect Bank Account
            <Sparkles className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* View All Analytics Link - hidden on mobile where chart is not shown, and when filtered */}
      {showChart && (
        <div className="hidden md:flex justify-end mt-4">
        <Link 
          to="/analytics" 
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          View all analytics
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      )}
    </div>
  );
};
